import React, { useState, useEffect } from 'react';
import { useCPRLog } from './CPRLog';
import { loadCurrentState, saveCurrentState } from './CprState/storage';
import { cprEventEmitter, EVENTS } from './cprEvents';
import './MedicalProcedures.css';
import { useTranslation } from 'react-i18next';
import { useResusContext } from '../Resus/ResusContext';
import MedicalProceduresRecommendedValues from './MedicalProceduresRecommendedValues';

interface ProcedureLog {
  [key: string]: string;
}

const MedicalProcedures: React.FC = () => {
  const { addEntry } = useCPRLog();
  const { t } = useTranslation(['resus']);
  const [lastPerformed, setLastPerformed] = useState<ProcedureLog>(() => {
    const savedState = loadCurrentState();
    return savedState?.procedures?.lastPerformed || {};
  });

  const { age } = useResusContext();

  useEffect(() => {
    const unsubscribe = cprEventEmitter.subscribe(EVENTS.RESET_CPR, () => {
      setLastPerformed({});
    });

    return () => unsubscribe();
  }, []);

  const procedures = [
    { id: 'catheter', label: t('procedures.catheter'), logText: t('procedures.logCatheter')},    
    { id: 'nasogastric', label: t('procedures.nasogastric'), logText: t('procedures.logNasogastric')},
    { id: 'iv', label: 'IV', logText: t('procedures.logIV'), group: 'line' },
    { id: 'io', label: 'IO', logText: t('procedures.logIO'), group: 'line' },
    { id: 'central', label: 'CENTRAL', logText: t('procedures.logCentral'), group: 'line' },
  ];

  const handleProcedureClick = (procedure: { id: string; logText: string }) => {
    const timestamp = new Date().toISOString();
    
    // Update local state
    const newLastPerformed = { 
      ...lastPerformed, 
      [procedure.id]: timestamp 
    };
    setLastPerformed(newLastPerformed);

    // Save to storage
    saveCurrentState({
      procedures: {
        lastPerformed: newLastPerformed
      }
    }, 'procedures');

    addEntry({
      timestamp,
      text: procedure.logText,
      type: 'action',
      isImportant: false,
    });
  };

  const renderProcedureButton = (procedure: any) => (
    <div key={procedure.id} className="procedure-button-container">
      <button
        data-testid={`medical-procedures-${procedure.id}`}
        onClick={() => handleProcedureClick(procedure)}>
        {procedure.label}
      </button>
      {lastPerformed[procedure.id] && (
        <span className="last-performed">
          {t('procedures.lastPerformedAt')} {new Date(lastPerformed[procedure.id]).toLocaleTimeString('he-IL')}
        </span>
      )}
    </div>
  );

  return (
    <div className="medical-procedures">
      {age !== null && age !== undefined && <MedicalProceduresRecommendedValues />}
      <h5>{t('procedures.clickToRecord')}:</h5>      
      <div className="procedures-grid">
        {procedures.filter(p => !p.group).map(renderProcedureButton)}
      </div>
      <div className="line-procedures">
        <h5>{t('line.title')}</h5>
        <div className="procedures-grid">
          {procedures.filter(p => p.group === 'line').map(renderProcedureButton)}
        </div>
      </div>
    </div>
  );
};

export default MedicalProcedures;